import cx from 'classnames';
import { Field, Formik } from 'formik';
import { useContext, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import styled, { useTheme } from 'styled-components';
import * as Yup from 'yup';

import { updateUserDetails } from '../../../api/Optimise';
import { AppContext } from '../../../context/AppContext';
import RippleLoaderLines from '../../RippleLoaderLines';
import WhiteSpinner from '../../WhiteSpinner';

import InformationPanel from './InformationPanel';

const FlexRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
`;

const SpinnerWrapper = styled.div`
  padding-top: 14px;
`;

const StyledSelect = styled.select`
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  background-color: ${(props) => props.theme.colors.white};
  height: 41px;
  border-radius: 4px;
  border-color: ${(props) => props.theme.colors.light};
  font-size: 14px;
  padding: 6px 12px;
  text-align: left;

  &:focus {
    border-color: ${(props) => props.theme.colors.light};
    box-shadow: none;
    outline: none;
  }
  &[disabled] {
    background-color: #f8f9fa;
    opacity: 0.65;
  }
`;

const StyledApplyButton = styled(Button)`
  flex: 1;
  min-width: 0;
  height: 41px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
`;

const MinimumSOCSettings = ({ loadUser, setShowError }) => {
  const theme = useTheme();
  const { userDetails, userSub } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingCount, setIsSavingCount] = useState(0);

  useEffect(() => {
    if (!!userDetails) {
      setIsLoading(false);
    }
  }, [userDetails]);

  const incrementSavingCount = () => {
    setIsSavingCount((prevCount) => prevCount + 1);
  };

  const decrementSavingCount = () => {
    setIsSavingCount((prevCount) => Math.max(prevCount - 1, 0));
  };

  const percentageOptions = Array.from({ length: 19 }, (_, i) => 10 + i * 5);
  const initialValue = userDetails?.battery_details.soc_lower_limit || 10;

  const socSchema = Yup.object().shape({
    soc: Yup.number().oneOf(percentageOptions, 'Please select a value').required('Please select a value'),
  });

  const onSubmit = ({ soc }, { setSubmitting }) => {
    setSubmitting(true);
    incrementSavingCount();
    updateUserDetails(
      {
        battery_soc_lower_limit: soc,
        battery_soc_upper_limit: 100,
        simulation_mode: userDetails?.simulation_mode,
      },
      userSub
    )
      .then(() => {
        decrementSavingCount();
        loadUser();
      })
      .catch((e) => {
        console.error(e);
        decrementSavingCount();
        setShowError(true);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <InformationPanel title="Minimum battery discharge value" style={{ overflow: 'visible ' }}>
      <li>
        Set the lowest level your battery will discharge to. By default, this is set to 10% for battery health. If you
        want to store more for emergency reasons you can, but it will reduce the impact of {theme.language.appName}.
      </li>
      {isLoading ? (
        <RippleLoaderLines lines={1} className="my-3" />
      ) : (
        <Formik
          initialValues={{ soc: initialValue }}
          validationSchema={socSchema}
          onSubmit={onSubmit}
          validateOnBlur
          validateOnChange
        >
          {({ handleSubmit, values, isSubmitting, errors, touched }) => (
            <Form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <FlexRow>
                <Field name="soc">
                  {({ field, meta }) => (
                    <Form.Group controlId="soc" style={{ flex: 1, marginBottom: 0 }}>
                      <StyledSelect
                        as="select"
                        size="md"
                        {...field}
                        isValid={!meta.error && meta.touched}
                        isInvalid={!!meta.error && meta.touched}
                        className={cx({ error: !!meta.error && meta.touched })}
                        disabled={isLoading}
                      >
                        {percentageOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}%
                          </option>
                        ))}
                      </StyledSelect>
                      {meta.error && meta.touched && (
                        <div style={{ marginTop: 4 }}>
                          <span style={{ color: theme.colors.danger, fontSize: 13 }}>{meta.error}</span>
                        </div>
                      )}
                    </Form.Group>
                  )}
                </Field>
                <StyledApplyButton
                  type="submit"
                  variant="primary"
                  disabled={isLoading || isSavingCount || isSubmitting || values.soc === initialValue || !!errors.soc}
                >
                  <span>{isSavingCount ? <WhiteSpinner /> : 'Apply'}</span>
                </StyledApplyButton>
              </FlexRow>
            </Form>
          )}
        </Formik>
      )}
    </InformationPanel>
  );
};

export default MinimumSOCSettings;
